import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Spinner, formatDate } from '@dogdisk/shared'
import { AgentLayout } from '../components/agent-layout'
import { useAgentDashboard, type AgentTxRow } from '../_lib/agent-api'

// y9B7q pen frame: 时间窗筛选(7 / 30 / 90 天)
type TimeWindow = '7d' | '30d' | '90d'

const ORANGE = '#F59E0B'

// 分账类型 → 中文标签 + 颜色（与 .pen donutCard 一致）
const REF_TYPE_META: Record<string, { label: string; color: string }> = {
  vip: { label: 'VIP 充值', color: '#F59E0B' },
  vip_recharge: { label: 'VIP 充值', color: '#F59E0B' },
  pack: { label: '资源包订阅', color: '#FB923C' },
  resource_pack: { label: '资源包订阅', color: '#FB923C' },
  content: { label: '内容奖励', color: '#10B981' },
  reward: { label: '内容奖励', color: '#10B981' },
}

function refTypeLabel(t: string): string {
  return REF_TYPE_META[t]?.label ?? t
}

export function AgentDashboardPage() {
  const navigate = useNavigate()
  const { data, isLoading } = useAgentDashboard()
  const [window, setWindow] = useState<TimeWindow>('30d')
  const [activeSegment, setActiveSegment] = useState<string | null>(null)

  // 根据时间窗筛选趋势数据(纯前端 slice;真后端 endpoint 支持 ?window=… 待 §A5)
  const trendFull = data?.trend_30d ?? []
  const trend = useMemo(() => {
    const days = window === '7d' ? 7 : window === '90d' ? 90 : 30
    return trendFull.slice(-days)
  }, [trendFull, window])

  // 分账类型分布（按 reference_type 聚合 amount）
  const donutData = useMemo(() => {
    const acc = new Map<string, number>()
    for (const tx of data?.recent_transactions ?? []) {
      acc.set(tx.reference_type, (acc.get(tx.reference_type) ?? 0) + tx.amount_coin)
    }
    const arr = Array.from(acc.entries()).map(([key, value]) => ({
      name: refTypeLabel(key),
      value,
      color: REF_TYPE_META[key]?.color ?? '#9CA3AF',
    }))
    return arr.length > 0
      ? arr
      : [
          { name: 'VIP 充值', value: 50, color: '#F59E0B' },
          { name: '资源包订阅', value: 30, color: '#FB923C' },
          { name: '内容奖励', value: 20, color: '#10B981' },
        ]
  }, [data?.recent_transactions])

  return (
    <AgentLayout>
      <div data-testid="ipLI2" className="flex flex-col gap-5 p-6">
        {/* KPI 卡片 4 列 — 点击跳详情页 */}
        <section className="grid grid-cols-4 gap-4">
          <button
            type="button"
            data-testid="agent-kpi-balance"
            onClick={() => navigate('/withdraw')}
            className="text-left"
          >
            <KpiCard
              label="当前余额"
              value={(data?.kpi.current_balance_coin ?? 0).toLocaleString()}
              unit="COIN"
              sub={`≈ ¥${((data?.kpi.balance_in_cny ?? 0) / 100).toFixed(2)}`}
            />
          </button>
          <button
            type="button"
            data-testid="agent-kpi-month"
            onClick={() => navigate('/transactions')}
            className="text-left"
          >
            <KpiCard
              label="本月分成"
              value={(data?.kpi.month_commission_coin ?? 0).toLocaleString()}
              unit="COIN"
            />
          </button>
          <button
            type="button"
            data-testid="agent-kpi-total"
            onClick={() => navigate('/transactions')}
            className="text-left"
          >
            <KpiCard
              label="累计分成"
              value={(data?.kpi.total_commission_coin ?? 0).toLocaleString()}
              unit="COIN"
            />
          </button>
          <button
            type="button"
            data-testid="agent-kpi-users"
            onClick={() => navigate('/users')}
            className="text-left"
          >
            <KpiCard
              label="我的下级用户"
              value={(data?.kpi.my_users_count ?? 0).toString()}
              unit="人"
            />
          </button>
        </section>

        {/* 趋势 + 饼图 */}
        <section className="grid grid-cols-[1fr_360px] gap-4">
          <div
            className="flex h-[280px] flex-col gap-3 rounded-xl border border-gray-200 bg-white p-5"
          >
            <header className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">
                近 {window === '7d' ? 7 : window === '90d' ? 90 : 30} 天分成趋势
              </h2>
              <TimeWindowSwitch value={window} onChange={setWindow} />
            </header>
            {isLoading ? (
              <Spinner className="text-amber-500" />
            ) : trend.length > 0 ? (
              <div className="-mx-2 h-full w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend} margin={{ top: 6, right: 8, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={ORANGE} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={ORANGE} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#9CA3AF' }}
                      tickLine={false}
                      axisLine={{ stroke: '#E5E7EB' }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#9CA3AF' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: '1px solid #E5E7EB',
                        fontSize: 12,
                      }}
                      cursor={{ stroke: '#FBBF24', strokeWidth: 1, strokeDasharray: '3 3' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="commission_coin"
                      stroke={ORANGE}
                      strokeWidth={2.5}
                      fill="url(#trendFill)"
                      activeDot={{ r: 4, fill: ORANGE, stroke: '#fff', strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="flex flex-1 items-center justify-center text-sm text-gray-400">
                暂无趋势数据
              </p>
            )}
          </div>

          <div className="flex h-[280px] flex-col gap-3 rounded-xl border border-gray-200 bg-white p-5">
            <h2 className="text-sm font-semibold text-gray-900">分账类型分布</h2>
            <div className="flex flex-1 items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    innerRadius={42}
                    outerRadius={64}
                    paddingAngle={2}
                    stroke="#fff"
                    strokeWidth={2}
                    onClick={(slice) => {
                      const name = (slice as { name?: string })?.name
                      if (!name) return
                      setActiveSegment((cur) => (cur === name ? null : name))
                    }}
                  >
                    {donutData.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={entry.color}
                        opacity={activeSegment && activeSegment !== entry.name ? 0.25 : 1}
                        style={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: '1px solid #E5E7EB',
                      fontSize: 12,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="flex flex-col gap-1.5 text-xs text-gray-600">
              {donutData.map((d) => {
                const total = donutData.reduce((s, x) => s + x.value, 0)
                const pct = total > 0 ? Math.round((d.value / total) * 100) : 0
                const dimmed = activeSegment !== null && activeSegment !== d.name
                return (
                  <li key={d.name}>
                    <button
                      type="button"
                      data-testid={`agent-donut-legend-${d.name}`}
                      onClick={() =>
                        setActiveSegment((cur) => (cur === d.name ? null : d.name))
                      }
                      className={`flex w-full items-center justify-between ${
                        dimmed ? 'opacity-40' : ''
                      } hover:opacity-100`}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: d.color }}
                        />
                        {d.name}
                      </span>
                      <span className="font-medium tabular-nums">{pct}%</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        </section>

        {/* Donut 单段过滤态(pen frame con4F) — 选中某 segment 时展示 */}
        {activeSegment && (
          <DonutFilterCard
            segmentName={activeSegment}
            onClear={() => setActiveSegment(null)}
          />
        )}

        {/* 最近分账流水 */}
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <header className="flex items-center justify-between pb-3">
            <h2 className="text-sm font-semibold text-gray-900">最近分账流水</h2>
            <button
              type="button"
              onClick={() => navigate('/transactions')}
              className="text-xs font-medium text-amber-500 hover:text-amber-600"
            >
              查看全部 →
            </button>
          </header>

          {isLoading ? (
            <Spinner className="text-amber-500" />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-semibold text-gray-500">
                  <th className="py-2.5 font-semibold">分账类型</th>
                  <th className="py-2.5 font-semibold">来源用户</th>
                  <th className="py-2.5 font-semibold">金额</th>
                  <th className="py-2.5 font-semibold">时间</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent_transactions ?? [])
                  .filter((tx) => {
                    if (!activeSegment) return true
                    return refTypeLabel(tx.reference_type) === activeSegment
                  })
                  .slice(0, 8)
                  .map((tx) => (
                    <RecentRow
                      key={tx.id}
                      tx={tx}
                      onClick={() => navigate(`/transactions?focus=${tx.id}`)}
                    />
                  ))}
                {data?.recent_transactions.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-sm text-gray-400">
                      暂无分账流水
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </AgentLayout>
  )
}

function KpiCard({
  label,
  value,
  unit,
  sub,
}: {
  label: string
  value: string
  unit?: string
  sub?: string
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-xl border border-gray-200 bg-white p-5">
      <p className="text-[13px] text-gray-500">{label}</p>
      <p className="flex items-baseline gap-1.5">
        <span className="text-[22px] font-bold tabular-nums text-gray-900">{value}</span>
        {unit && <span className="text-sm font-medium text-amber-500">{unit}</span>}
      </p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  )
}

// TimeWindowSwitch — pen frame y9B7q (切换器-趋势图时间窗)
function TimeWindowSwitch({
  value,
  onChange,
}: {
  value: TimeWindow
  onChange: (v: TimeWindow) => void
}) {
  const options: Array<{ key: TimeWindow; label: string }> = [
    { key: '7d', label: '7 天' },
    { key: '30d', label: '30 天' },
    { key: '90d', label: '90 天' },
  ]
  return (
    <div
      data-testid="y9B7q"
      data-frame-id="y9B7q"
      className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white p-0.5"
      role="tablist"
    >
      {options.map((o) => {
        const active = o.key === value
        return (
          <button
            key={o.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.key)}
            className={[
              'rounded px-3 py-1 text-[12px] font-medium transition-colors',
              active ? 'text-white' : 'text-gray-600 hover:bg-gray-100',
            ].join(' ')}
            style={active ? { backgroundColor: '#1F2937' } : undefined}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

// DonutFilterCard — pen frame con4F (Donut-单段过滤态)
function DonutFilterCard({
  segmentName,
  onClear,
}: {
  segmentName: string
  onClear: () => void
}) {
  return (
    <section
      data-testid="con4F"
      data-frame-id="con4F"
      className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3"
    >
      <div className="flex flex-col gap-0.5">
        <p className="text-[13px] font-medium text-gray-800">
          已应用过滤:类型 = <span className="font-semibold">{segmentName}</span>
        </p>
        <p className="text-[11px] text-gray-500">
          上方 donut 高亮当前 segment,下方流水只显示该 segment 行
        </p>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-200"
      >
        <X size={12} />
        清除筛选
      </button>
    </section>
  )
}

function RecentRow({ tx, onClick }: { tx: AgentTxRow; onClick: () => void }) {
  return (
    <tr
      data-testid={`agent-recent-row-${tx.id}`}
      onClick={onClick}
      className="cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
    >
      <td className="py-2.5 text-gray-700">{refTypeLabel(tx.reference_type)}</td>
      <td className="py-2.5 text-gray-500">用户 {tx.from_user_username_masked}</td>
      <td className="py-2.5 font-semibold tabular-nums text-amber-500">
        +{tx.amount_coin.toLocaleString()} COIN
      </td>
      <td className="py-2.5 text-gray-400">{formatDate(tx.created_at)}</td>
    </tr>
  )
}
